'use client'

import { useState, useEffect, useCallback } from 'react'
import Image from 'next/image'
import { SECTION_HEADLINE_TRANSFORM } from '../../theme/sectionTypography'
import Link from 'next/link'
import { ChevronLeft, ChevronRight } from 'lucide-react'

export type HeroSlide = {
  imageUrl: string
  imageAlt?: string
  headline?: string
  subheadline?: string
  ctaText?: string
  ctaHref?: string
}

export type HeroCarouselProps = {
  slides: HeroSlide[]
  /** Auto-advance interval in ms (default 6000). Set 0 to disable. */
  autoAdvance?: number
  align?: 'center' | 'left' | 'bottom-left'
}

export function HeroCarousel({
  slides,
  autoAdvance = 6000,
  align = 'center',
}: HeroCarouselProps) {
  const [current, setCurrent] = useState(0)
  const [paused, setPaused] = useState(false)

  const next = useCallback(() => {
    setCurrent((c) => (c + 1) % slides.length)
  }, [slides.length])

  const prev = useCallback(() => {
    setCurrent((c) => (c - 1 + slides.length) % slides.length)
  }, [slides.length])

  useEffect(() => {
    if (!autoAdvance || paused || slides.length <= 1) return
    const timer = setInterval(next, autoAdvance)
    return () => clearInterval(timer)
  }, [autoAdvance, paused, next, slides.length])

  if (slides.length === 0) return null

  // Fall back to static HeroImage-style if only one slide
  const slide = slides[current]

  const alignClasses = {
    center: 'items-center justify-center text-center',
    left: 'items-center justify-start text-left pl-8 md:pl-16',
    'bottom-left': 'items-end justify-start text-left pl-8 md:pl-16 pb-12 md:pb-16',
  }

  return (
    <section
      className="relative w-full min-h-[60vh] md:min-h-[70vh] overflow-hidden bg-gray-900"
      data-component="HeroCarousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Slides */}
      {slides.map((s, i) => (
        <div
          key={i}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === current ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          <Image
            src={s.imageUrl}
            alt={s.imageAlt ?? ''}
            fill
            sizes="100vw"
            className="object-cover"
            priority={i === 0}
            // See HeroImage — `priority` does not imply fetchpriority. Only
            // the first slide is the LCP candidate; siblings stay default.
            fetchPriority={i === 0 ? 'high' : undefined}
          />
        </div>
      ))}

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-black/10 z-[1]" />

      {/* Content */}
      <div className={`relative z-10 flex flex-col ${alignClasses[align]} min-h-[60vh] md:min-h-[70vh] max-w-[var(--max-width-page)] mx-auto px-4`}>
        {slide.headline && (
          <h1
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white max-w-3xl leading-tight"
            style={{ fontFamily: 'var(--font-heading)', textTransform: SECTION_HEADLINE_TRANSFORM }}
          >
            {slide.headline}
          </h1>
        )}
        {slide.subheadline && (
          <p className="text-lg md:text-xl text-white/85 max-w-2xl mt-4" style={{ fontFamily: 'var(--font-body)' }}>
            {slide.subheadline}
          </p>
        )}
        {slide.ctaText && slide.ctaHref && (
          <Link
            href={slide.ctaHref}
            className="mt-6 inline-block bg-[var(--color-cta)] text-[var(--color-cta-text)] px-8 py-3 rounded-[var(--radius-lg)] font-medium text-lg hover:opacity-90 transition-opacity"
          >
            {slide.ctaText}
          </Link>
        )}
      </div>

      {/* Navigation arrows */}
      {slides.length > 1 && (
        <>
          <button
            onClick={prev}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-white/20 backdrop-blur-sm text-white p-2 rounded-full hover:bg-white/30 transition-colors"
            aria-label="Previous slide"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={next}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-white/20 backdrop-blur-sm text-white p-2 rounded-full hover:bg-white/30 transition-colors"
            aria-label="Next slide"
          >
            <ChevronRight size={20} />
          </button>

          {/* Dots */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className={`w-2.5 h-2.5 rounded-full transition-colors ${
                  i === current ? 'bg-white' : 'bg-white/40 hover:bg-white/60'
                }`}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  )
}
